import React, { useState, useEffect, useCallback, useRef } from "react";
import { Grid, Button, Typography, CircularProgress, Divider } from "@material-ui/core";
import { WhatsApp as WhatsAppIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { getFacebookConfig, connectWaba } from "../../services/facebookOnboarding.api";
import loadFacebookSdk from "../../helpers/loadFacebookSdk";
import toastError from "../../errors/toastError";

/**
 * Embedded Signup de WhatsApp (Meta Cloud API) — doc 0386. Un clic:
 *   FB.login (config_id de WhatsApp + extras de embedded signup) → popup de Meta donde el usuario
 *   elige/crea su WABA + número → un listener `message` captura waba_id + phone_number_id → el
 *   callback devuelve un `code` → el backend lo canjea por el business token y crea la conexión.
 * Reutiliza el cargador del SDK y el endpoint /facebook-onboarding/config de FB/IG (que ahora
 * también devuelve wabaConfigId). El usuario NO pega ninguna credencial.
 */
export default function WabaEmbeddedSignup({ onConnected }) {
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [initError, setInitError] = useState("");
  const [loading, setLoading] = useState(false);
  // Meta entrega el waba_id + phone_number_id por un evento `message` (antes del callback de FB.login).
  const sessionInfo = useRef({ wabaId: "", phoneNumberId: "" });

  const init = useCallback(async () => {
    setInitError("");
    setSdkReady(false);
    try {
      const { data } = await getFacebookConfig();
      setConfig(data);
      await loadFacebookSdk(data.appId, data.graphVersion);
      setSdkReady(true);
    } catch (err) {
      setInitError(err?.message || "No se pudo preparar el acceso de Meta.");
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  // Listener del Embedded Signup: Meta postea { type:'WA_EMBEDDED_SIGNUP', event, data:{ waba_id, phone_number_id } }.
  useEffect(() => {
    const onMessage = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return; // Meta también postea mensajes no-JSON; se ignoran.
      }
      if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (payload.data?.waba_id || payload.data?.phone_number_id) {
        sessionInfo.current = {
          wabaId: payload.data?.waba_id || sessionInfo.current.wabaId,
          phoneNumberId: payload.data?.phone_number_id || sessionInfo.current.phoneNumberId,
        };
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const handleLaunch = useCallback(() => {
    if (!window.FB) {
      toast.error("El inicio de sesión con Facebook no está disponible: falta configurar la app de Meta en Configuración → Integraciones.");
      return;
    }
    if (!config?.wabaConfigId) {
      toast.error(
        "Falta la configuración de Embedded Signup (META_WABA_ES_CONFIG_ID en el servidor)."
      );
      return;
    }
    setLoading(true);
    // OJO: el SDK rechaza callbacks async; el callback es una función normal y la parte async va adentro.
    window.FB.login(
      (response) => {
        (async () => {
          try {
            const code = response?.authResponse?.code;
            if (!code) {
              setLoading(false);
              return; // el usuario cerró el popup
            }
            const { wabaId, phoneNumberId } = sessionInfo.current;
            if (!wabaId || !phoneNumberId) {
              toast.error("Meta no devolvió el WABA/número. Reintenta y completa el registro.");
              setLoading(false);
              return;
            }
            await connectWaba({ code, wabaId, phoneNumberId });
            toast.success("✅ WhatsApp Oficial conectado y webhook suscrito.");
            if (onConnected) onConnected();
          } catch (err) {
            toastError(err);
          } finally {
            setLoading(false);
          }
        })();
      },
      {
        config_id: config.wabaConfigId,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
      }
    );
  }, [config, onConnected]);

  // Oculto hasta que Meta habilite el activo de WhatsApp para la app (Tech Provider + App Review):
  // mientras el gate no se abra (META_WABA_ES_ENABLED != "true"), el modal WABA muestra solo el
  // método manual. Se reactiva sin código nuevo con el flag del backend. Doc 0386.
  if (!config?.wabaEsEnabled) return null;

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 6 }}>
        Recomendado — conexión en un clic
      </Typography>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
        onClick={handleLaunch}
        disabled={loading || !sdkReady}
        style={{ backgroundColor: "#25D366", color: "#fff", textTransform: "none", fontWeight: 600 }}
        fullWidth
      >
        {loading ? "Conectando con Meta…" : "Conectar con Meta (un clic)"}
      </Button>
      <Typography
        variant="caption"
        color={initError ? "error" : "textSecondary"}
        style={{ marginTop: 6, display: "block" }}
      >
        {initError
          ? `⚠️ ${initError}`
          : sdkReady
          ? "Elige o crea tu WhatsApp Business dentro de Meta — sin pegar credenciales."
          : "Preparando el acceso de Meta…"}
      </Typography>
      <Divider style={{ margin: "14px 0 8px" }} />
      <Typography variant="caption" style={{ color: "#888" }}>
        ¿Prefieres el método avanzado? Ingresa las credenciales manualmente:
      </Typography>
    </Grid>
  );
}
